import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Col, Form, Input, Row, Space, Switch } from 'antd';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import Modal from '../../../main/components/Modal';
import { SELECT_STYLES } from '../../../main/utils/const';
import SelectEstablishment from '../Selects/SelectEstablishment';
import generatorPassword from '../../../main/utils/generatorPassword';
import SelectMultipleEstablishments from '../Selects/SelectMultipleEstablishments';
import { USER_ROLES } from '../../utils/factories';

const ModalCreateAdminAndLecturer = ({
  locales,
  onClose = () => {},
  visible = false,
  uploading = false,
  onCreate = () => {},
}) => {
  const [form] = Form.useForm();
  const { establishmentId = null } = useParams();
  const { user } = useSelector((state) => state);

  const [establishmentOptions, setEstablishmentOptions] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');

  const {
    establishments: {
      data: establishmentList,
      loading: establishmentOptionsLoading,
      createdEstablishments,
    },
    user: { current },
  } = useSelector((state) => state);

  const {
    establishments: {
      fetchEstablishments,
      addCreatedEstablishment,
      removeCreatedEstablishment,
    },
  } = useDispatch();

  useEffect(() => {
    if (current.role === 'super_admin')
      fetchEstablishments({
        page: 1,
        limit: 100000000,
      });
  }, [current]);

  useEffect(() => {
    if (establishmentList) {
      setEstablishmentOptions(establishmentList);
    }
  }, [establishmentList]);

  const handleSelect = (eId) => {
    addCreatedEstablishment({ establishmentId: eId });
  };

  const handleDelete = (eId) => {
    removeCreatedEstablishment({ establishmentId: eId });
  };

  const handleRoleChange = (role) => {
    setSelectedRole(role.value);
  };

  const handleFinish = (values) => {
    if (Array.isArray(values.establishment_id)) {
      const [selectedEstablishmentId] = values.establishment_id;
      values.establishment_id = selectedEstablishmentId;
    }

    if (typeof values.role === 'object') {
      values.role = values.role.value;
    }

    values.establishment_ids = createdEstablishments.map((e) => e.id);
    const afterSuccess = () => {
      form.resetFields();
    };
    onCreate(values, afterSuccess);
  };

  const handleAutoGeneratePassword = (value) => {
    if (value) form.setFieldsValue({ password: generatorPassword() });
  };

  return (
    <Modal
      isOpen={visible}
      title={locales.admin.adminEducatorDashboard.createNewAdminEducator}
      onClose={onClose}
    >
      <Form
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          establishment_id: Number(establishmentId),
          role: establishmentId ? 'lecturer' : null,
        }}
        form={form}
      >
        <div className="mt-20">
          <Row gutter={20}>
            <Col span={12}>
              <Form.Item
                label={locales.admin.adminEducatorDashboard.firstName}
                name="first_name"
                className="admin-input-label"
                rules={[
                  {
                    required: true,
                    message:
                      locales.admin.adminEducatorDashboard.firstNameIsRequired,
                  },
                ]}
              >
                <Input
                  placeholder={locales.admin.adminEducatorDashboard.firstName}
                  className="admin-input"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={locales.admin.adminEducatorDashboard.surname}
                name="surname"
                className="admin-input-label"
                rules={[
                  {
                    required: true,
                    message:
                      locales.admin.adminEducatorDashboard.surnameIsRequired,
                  },
                ]}
              >
                <Input
                  placeholder={locales.admin.adminEducatorDashboard.surname}
                  className="admin-input"
                />
              </Form.Item>
            </Col>
          </Row>
          {user?.current?.role === 'super_admin' ? (
            <Form.Item
              label={locales.admin.adminEducatorDashboard.role}
              name="role"
              className="admin-input-label"
              rules={[
                {
                  required: true,
                  message: locales.admin.adminEducatorDashboard.roleIsRequired,
                },
              ]}
            >
              <Select
                options={USER_ROLES.filter(
                  (role) =>
                    role.value !== 'student' && role.value !== 'super_admin',
                )}
                className="bmc-form-select"
                styles={SELECT_STYLES}
                placeholder={locales.admin.adminEducatorDashboard.chooseType}
                onChange={handleRoleChange}
              />
            </Form.Item>
          ) : (
            <Form.Item name="role" hidden>
              <Input />
            </Form.Item>
          )}
          <Form.Item
            label={locales.admin.adminEducatorDashboard.email}
            name="email"
            className="admin-input-label"
            rules={[
              {
                required: true,
                type: 'email',
                message: locales.admin.adminEducatorDashboard.emailIsRequired,
              },
            ]}
          >
            <Input
              placeholder={locales.admin.adminEducatorDashboard.addUserEmail}
              className="admin-input"
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            label={locales.admin.adminEducatorDashboard.password}
            name="password"
            className="admin-input-label"
            rules={[
              {
                required: true,
                message:
                  locales.admin.adminEducatorDashboard.passwordIsRequired,
              },
              {
                min: 8,
                message: locales.admin.adminEducatorDashboard.passwordMinChar,
              },
            ]}
          >
            <Input.Password
              placeholder={
                locales.admin.adminEducatorDashboard.createUserPassword
              }
              className="admin-input"
              autoComplete="new-password"
            />
          </Form.Item>
          <Space>
            <Switch
              className="admin-switch"
              onChange={handleAutoGeneratePassword}
            />
            <span className="admin-switch-title">
              {
                locales.admin.adminEducatorDashboard
                  .automaticallyGeneratePassword
              }
            </span>
          </Space>
        </div>
        {establishmentId === null && selectedRole === 'lecturer' ? (
          <div className="mt-20">
            <Form.Item
              label={locales.admin.adminEducatorDashboard.establishment}
              name="establishment_id"
              className="admin-input-label"
              rules={[
                {
                  required: true,
                  message:
                    locales.admin.adminEducatorDashboard
                      .establishmentIsRequired,
                },
              ]}
            >
              <SelectEstablishment />
            </Form.Item>
          </div>
        ) : (
          <Form.Item
            name="establishment_id"
            className="admin-input-label"
            hidden
          >
            <Input />
          </Form.Item>
        )}
        {selectedRole === 'admin' && (
          <div className="mt-20">
            <SelectMultipleEstablishments
              establishments={establishmentOptions}
              loading={uploading || establishmentOptionsLoading}
              labelOfSelect={
                locales.admin.adminEducatorDashboard.inviteUsersToEstablishment
              }
              placeholderOfSelect={
                locales.admin.adminEducatorDashboard.chooseEstablishmentByName
              }
              labelOfList={locales.admin.adminEducatorDashboard.establishments}
              selectedIds={createdEstablishments.map(
                (establishment) => establishment.id,
              )}
              onSelect={handleSelect}
              onDelete={handleDelete}
            />
          </div>
        )}
        <div className="flex align-right mt-40">
          <Space>
            <Button onClick={onClose} disabled={uploading}>
              {locales.common.cancel}
            </Button>
            <Button type="primary" htmlType="submit" loading={uploading}>
              {locales.common.create}
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

ModalCreateAdminAndLecturer.propTypes = {
  onClose: PropTypes.func,
  visible: PropTypes.bool,
  uploading: PropTypes.bool,
};
export default ModalCreateAdminAndLecturer;
